import React, { useState } from 'react';
import classNames from 'classnames';
import { Shield, ChevronDown } from 'lucide-react';
import { ListingCard } from '../../../components';
import PartnerExtraCard from '../../../components/PartnerExtraCard/PartnerExtraCard';
import { logRecommendationEvent } from '../../../util/api';

import css from './SectionCrossSell.module.css';

/**
 * SectionCrossSell — collapsible, grouped cross-sell panel for the listing page order column.
 *
 * Collapsed state shows a header with thumbnail previews.
 * Expanded state shows items organised by group (Protect, Power and Charge, Enhance).
 *
 * Backward compatible: if `groups` prop is absent, falls back to flat `listings` rendering.
 *
 * @param {Object} props
 * @param {string} props.heading - Contextual heading (e.g. "Extras for your iPhone 15 Pro")
 * @param {Array} [props.groups] - Grouped cross-sell data: [{ type, label, items, partnerExtras }]
 * @param {Array} [props.listings] - Flat cross-sell listings (backward compat)
 * @param {Array} [props.partnerExtras] - Partner service extras
 * @param {boolean} props.isLoading - Loading state
 * @param {string} [props.sourceListingId] - The listing being viewed (for logging)
 * @param {string} [props.className] - Additional CSS class
 */
const SectionCrossSell = props => {
  const {
    heading,
    groups = [],
    listings = [],
    partnerExtras = [],
    isLoading = false,
    sourceListingId,
    className,
  } = props;

  const [expanded, setExpanded] = useState(false);

  // Determine if we have grouped data or just flat listings
  const hasGroups = groups.length > 0;
  const hasListings = listings.length > 0;
  const hasPartnerExtras = partnerExtras.length > 0;
  const hasContent = hasGroups || hasListings || hasPartnerExtras;

  // Don't render if no items and not loading
  if (!isLoading && !hasContent) {
    return null;
  }

  // Collect all item IDs for impression logging
  const allItemIds = hasGroups
    ? groups.flatMap(g => (g.items || []).map(l => l.id?.uuid))
    : listings.map(l => l.id?.uuid);

  const handleToggle = () => {
    const willExpand = !expanded;
    setExpanded(willExpand);

    if (willExpand && allItemIds.length > 0) {
      logRecommendationEvent({
        userId: 'anonymous',
        listingId: sourceListingId,
        action: 'cross_sell_impression',
        metadata: {
          sourceListingId,
          suggestedIds: allItemIds,
          groupTypes: hasGroups ? groups.map(g => g.type) : [],
          touchpoint: 'section',
        },
      });

      // Log partner extra impressions separately
      if (hasPartnerExtras) {
        for (const extra of partnerExtras) {
          logRecommendationEvent({
            userId: 'anonymous',
            listingId: sourceListingId,
            action: 'partner_extra_impression',
            metadata: {
              sourceListingId,
              partnerId: extra.id,
              partnerLabel: extra.label,
              isPartnerExtra: true,
              touchpoint: 'section',
            },
          });
        }
      }
    }
  };

  // Get first 3 thumbnail URLs for collapsed preview (from first non-empty group or flat list)
  const thumbnailSource = hasGroups
    ? groups.flatMap(g => g.items || [])
    : listings;

  const thumbnailUrls = thumbnailSource.slice(0, 3).map(listing => {
    const firstImage = listing.images?.[0];
    const variants = firstImage?.attributes?.variants || {};
    const variant = variants['listing-card'] || variants['listing-card-2x'];
    return variant?.url || null;
  }).filter(Boolean);

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.header} onClick={handleToggle} role="button" tabIndex={0}>
        <Shield className={css.headerIcon} size={18} strokeWidth={1.5} />
        <h3 className={css.heading}>{heading || 'Recommended accessories'}</h3>

        {!expanded && thumbnailUrls.length > 0 && (
          <div className={css.thumbnails}>
            {thumbnailUrls.map((url, i) => (
              <img key={i} src={url} alt="" className={css.thumbnail} loading="lazy" />
            ))}
          </div>
        )}

        <ChevronDown
          className={classNames(css.chevron, { [css.chevronExpanded]: expanded })}
          size={16}
        />
      </div>

      <div className={classNames(css.content, { [css.contentExpanded]: expanded })}>
        {isLoading ? (
          <div className={css.items}>
            <div className={css.skeletonBar} />
            <div className={css.skeletonBar} />
            <div className={css.skeletonBar} />
          </div>
        ) : hasGroups ? (
          /* Grouped rendering */
          <div className={css.groupedContent}>
            {groups.map(group => (
              <div key={group.type} className={css.group}>
                <span className={css.groupLabel}>{group.label}</span>
                <div className={css.groupItems}>
                  {(group.items || []).map((listing, index) => (
                    <div
                      key={listing.id?.uuid}
                      onClick={() => {
                        logRecommendationEvent({
                          userId: 'anonymous',
                          listingId: listing.id?.uuid,
                          action: 'cross_sell_click',
                          metadata: {
                            sourceListingId,
                            position: index + 1,
                            touchpoint: 'section',
                            groupType: group.type,
                            groupLabel: group.label,
                            isPartnerExtra: false,
                            partnerId: null,
                            neuralFraudScore: listing._neuralScores?.fraudScore ?? null,
                            neuralQualityScore: listing._neuralScores?.qualityScore ?? null,
                          },
                        });
                      }}
                    >
                      <ListingCard
                        listing={listing}
                        variant="compact"
                        showAuthorInfo={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Partner extras as a services group */}
            {hasPartnerExtras && (
              <div className={css.group}>
                <span className={css.groupLabel}>Services</span>
                <div className={css.groupItems}>
                  {partnerExtras.map(extra => (
                    <PartnerExtraCard
                      key={extra.id}
                      extra={extra}
                      sourceListingId={sourceListingId}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Flat rendering (backward compat) */
          <div className={css.items}>
            {listings.map((listing, index) => (
              <div
                key={listing.id?.uuid}
                onClick={() => {
                  logRecommendationEvent({
                    userId: 'anonymous',
                    listingId: listing.id?.uuid,
                    action: 'cross_sell_click',
                    metadata: {
                      sourceListingId,
                      position: index + 1,
                      touchpoint: 'section',
                    },
                  });
                }}
              >
                <ListingCard
                  listing={listing}
                  variant="compact"
                  showAuthorInfo={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionCrossSell;
